<template>
	<div class="callback-container">
		<div class="callback-box">
			<div v-if="loading" class="loading-content">
				<i class="el-icon-loading loading-icon"></i>
				<h2 class="loading-title">正在处理登录...</h2>
				<p class="loading-text">{{ statusText }}</p>
			</div>

			<div v-else-if="error" class="error-content">
				<i class="el-icon-circle-close error-icon"></i>
				<h2 class="error-title">登录失败</h2>
				<p class="error-text">{{ errorMessage }}</p>
				<div class="error-details" v-if="showErrorDetails">
					<p class="error-detail-text">URL参数: {{ JSON.stringify($route.query) }}</p>
					<p class="error-detail-text">Hash: {{ $route.hash }}</p>
				</div>
				<div class="error-actions">
					<el-button type="primary" @click="retryLogin" class="retry-button">
						重新登录
					</el-button>
					<el-button type="text" @click="showErrorDetails = !showErrorDetails" class="detail-button">
						{{ showErrorDetails ? '隐藏' : '显示' }}详细信息
					</el-button>
				</div>
			</div>

			<div v-else class="success-content">
				<i class="el-icon-circle-check success-icon"></i>
				<h2 class="success-title">登录成功</h2>
				<p class="success-text">正在跳转...</p>
			</div>
		</div>
	</div>
</template>

<script>
import { signinRedirectCallback } from '@/utils/oidcConfig'
import { getAccountInfo, setAccountInfo } from '@/utils/localdatas'

export default {
	name: 'SigninOidc',
	data() {
		return {
			loading: true,
			error: false,
			errorMessage: '',
			statusText: '正在验证身份信息...',
			returnUrl: null,
			showErrorDetails: false
		}
	},
	created() {
		this.handleCallback()
	},
	methods: {
		async handleCallback() {
			try {
				this.statusText = '正在验证身份信息...'
				
				// 检查URL参数
				const query = this.$route.query
				console.log('回调URL参数:', query)
				
				// 如果有错误参数，显示错误
				if (query.error) {
					throw new Error(query.error_description || query.error || '认证失败')
				}
				
				// 处理OIDC回调
				const user = await signinRedirectCallback()
				console.log('OIDC用户信息:', user)
				
				if (user) {
					this.statusText = '正在保存用户信息...'
					
					// 将OIDC用户信息转换为本地账户信息格式
					const accountInfo = this.convertUserToAccountInfo(user)
					console.log('转换后的账户信息:', accountInfo)
					setAccountInfo(accountInfo)
					
					this.statusText = '登录成功，正在跳转...'
					this.loading = false
					
					// 获取返回URL
					// state可能是字符串或对象
					let state = user.state
					if (typeof state === 'string') {
						try {
							state = JSON.parse(state)
						} catch (e) {
							// 如果不是JSON，可能是简单的URL字符串
							state = { returnUrl: state }
						}
					}
					
					this.returnUrl = (state && (state.returnUrl || state.returnurl)) || 
					                query.returnUrl || 
					                query.returnurl ||
					                '/'
					
					console.log('返回URL:', this.returnUrl)
					
					// 延迟跳转，让用户看到成功提示
					setTimeout(() => {
						this.redirect()
					}, 1000)
				} else {
					throw new Error('未获取到用户信息')
				}
			} catch (error) {
				console.error('登录回调处理失败:', error)
				console.error('错误详情:', {
					message: error.message,
					stack: error.stack,
					query: this.$route.query,
					hash: this.$route.hash
				})
				this.loading = false
				this.error = true
				
				// 显示更详细的错误信息
				let errorMsg = error.message || '登录验证失败，请重试'
				if (error.error) {
					errorMsg = `${errorMsg} (${error.error})`
				}
				this.errorMessage = errorMsg
			}
		},
		
		convertUserToAccountInfo(user) {
			// 将OIDC用户信息转换为本地账户信息格式
			const profile = user.profile || {}
			
			return {
				Account: profile.sub || profile.preferred_username || '',
				NickName: profile.name || profile.nickname || profile.preferred_username || '',
				Avatar: profile.picture || profile.avatar || '',
				Email: profile.email || '',
				CompanyCode: profile.company_code || '',
				CompanyName: profile.company_name || profile.organization || '',
				RoleID: profile.role_id || '',
				AccessToken: user.access_token || '',
				ExpiresAt: user.expires_at || null
			}
		},
		
		redirect() {
			try {
				if (!this.returnUrl) {
					this.$router.push('/')
					return
				}
				
				// 处理返回URL
				let returnUrl = this.returnUrl
				
				// 如果是编码的URL，先解码
				try {
					returnUrl = decodeURIComponent(returnUrl)
				} catch (e) {
					console.warn('URL解码失败，使用原始URL:', e)
				}
				
				// 如果是完整URL，直接跳转
				if (returnUrl.indexOf('http') !== -1 || returnUrl.indexOf('https') !== -1) {
					window.location.href = returnUrl
					return
				}
				
				// 如果是相对路径，使用路由跳转
				// 确保路径以 / 开头
				if (!returnUrl.startsWith('/')) {
					returnUrl = '/' + returnUrl
				}
				
				// 移除hash符号（如果存在）
				if (returnUrl.startsWith('/#')) {
					returnUrl = returnUrl.substring(2)
				}
				
				console.log('跳转到:', returnUrl)
				this.$router.push(returnUrl).catch(err => {
					console.error('路由跳转失败:', err)
					// 如果路由跳转失败，尝试直接修改location
					window.location.hash = returnUrl
				})
			} catch (e) {
				console.error('跳转失败:', e)
				// 如果所有跳转都失败，跳转到首页
				this.$router.push('/').catch(() => {
					window.location.href = '/'
				})
			}
		},
		
		retryLogin() {
			this.$router.push({
				path: '/Acccount/oidcLogin',
				query: {
					returnUrl: this.returnUrl || this.$route.query.returnUrl
				}
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.callback-container {
	min-height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	padding: 20px;
}

.callback-box {
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	padding: 60px 50px;
	width: 100%;
	max-width: 500px;
	text-align: center;
	animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.loading-content,
.error-content,
.success-content {
	display: flex;
	flex-direction: column;
	align-items: center;
}

.loading-icon,
.error-icon,
.success-icon {
	font-size: 64px;
	margin-bottom: 20px;
}

.loading-icon {
	color: #667eea;
	animation: spin 1s linear infinite;
}

.error-icon {
	color: #f56c6c;
}

.success-icon {
	color: #67c23a;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

.loading-title,
.error-title,
.success-title {
	font-size: 24px;
	font-weight: 600;
	margin: 0 0 15px 0;
	color: #333;
}

.loading-text,
.error-text,
.success-text {
	font-size: 16px;
	color: #666;
	margin: 0;
	line-height: 1.6;
}

.error-actions {
	margin-top: 30px;
	display: flex;
	flex-direction: column;
	gap: 15px;
	align-items: center;
}

.retry-button {
	padding: 12px 40px;
}

.detail-button {
	font-size: 14px;
}

.error-details {
	margin-top: 20px;
	padding: 15px;
	background: #f5f5f5;
	border-radius: 8px;
	text-align: left;
	max-width: 100%;
	overflow-x: auto;
}

.error-detail-text {
	font-size: 12px;
	color: #666;
	margin: 5px 0;
	word-break: break-all;
}

/* 响应式设计 */
@media (max-width: 768px) {
	.callback-box {
		padding: 40px 30px;
		margin: 20px;
	}

	.loading-title,
	.error-title,
	.success-title {
		font-size: 20px;
	}
}
</style>

